import { useEffect, useState } from 'react';
import {
  AlertCircle,
  Briefcase,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mail,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Search,
  XCircle,
} from 'lucide-react';
import { api, extractErrorMessage } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PrestataireFormModal } from '../components/prestataires/PrestataireFormModal';

/**
 * Page `/prestataires` — sous-module Interventions.
 *
 *  - Liste paginée avec recherche + filtre `is_active`.
 *  - Création / édition / désactivation (soft delete) via modal CRUD.
 *  - Permissions : `interventions.update` requis pour les actions d'écriture.
 */

interface PrestataireRow {
  id: string;
  nom: string;
  specialite: string | null;
  contact: {
    email?: string | null;
    telephone?: string | null;
    adresse?: string | null;
  } | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Pagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export function PrestatairesPage() {
  const { can } = usePermissions();
  const canRead = can('interventions', 'read');
  const canEdit = can('interventions', 'update');

  const [items, setItems] = useState<PrestataireRow[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [page, setPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeOnly]);

  useEffect(() => {
    if (!canRead) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeOnly, page, canRead]);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (activeOnly) params.set('is_active', 'true');
      params.set('page', String(page));
      params.set('page_size', '50');
      const res = await api.get<{ data: PrestataireRow[]; pagination: Pagination }>(
        `/prestataires?${params.toString()}`,
      );
      setItems(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditingId(null);
    setFormOpen(true);
  }

  function openEdit(id: string) {
    setEditingId(id);
    setFormOpen(true);
  }

  function handleSaved() {
    setFormOpen(false);
    setEditingId(null);
    refresh();
  }

  if (!canRead) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <p className="text-sm text-gray-500">Vous n'avez pas accès au module Interventions.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Briefcase className="w-7 h-7 mr-2 text-primary-600" />
            Prestataires
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {pagination
              ? `${pagination.total} prestataire${pagination.total > 1 ? 's' : ''}`
              : `${items.length} chargé(s)`}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={refresh}
            className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            title="Rafraîchir"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {canEdit && (
            <button
              onClick={openCreate}
              className="inline-flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau prestataire
            </button>
          )}
        </div>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex items-center justify-between flex-wrap gap-3">
        <label className="inline-flex items-center space-x-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span>Actifs uniquement</span>
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher (nom, spécialité)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-72 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom
                </th>
                <th className="hidden md:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Spécialité
                </th>
                <th className="hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Téléphone
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actif
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">
                    <Briefcase className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                    Aucun prestataire.
                    {canEdit && (
                      <p className="mt-1 text-xs">
                        Créez-en un pour pouvoir les affecter aux interventions externes.
                      </p>
                    )}
                  </td>
                </tr>
              ) : (
                items.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 font-medium text-gray-900">{p.nom}</td>
                    <td className="hidden md:table-cell px-3 py-2.5 text-gray-600">
                      {p.specialite ?? <span className="text-gray-400">—</span>}
                    </td>
                    <td className="hidden lg:table-cell px-3 py-2.5 text-gray-600">
                      {p.contact?.email ? (
                        <a
                          href={`mailto:${p.contact.email}`}
                          className="text-primary-600 hover:underline inline-flex items-center"
                        >
                          <Mail className="w-3 h-3 mr-1" />
                          {p.contact.email}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="hidden lg:table-cell px-3 py-2.5 text-gray-600">
                      {p.contact?.telephone ? (
                        <span className="inline-flex items-center">
                          <Phone className="w-3 h-3 mr-1 text-gray-400" />
                          {p.contact.telephone}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {p.is_active ? (
                        <span className="inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border bg-green-100 text-green-800 border-green-200">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Actif
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border bg-gray-100 text-gray-700 border-gray-200">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactif
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {canEdit && (
                        <button
                          onClick={() => openEdit(p.id)}
                          className="inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Modifier
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-gray-200 text-sm">
            <span className="text-gray-500">
              Page {pagination.page} sur {pagination.total_pages}
            </span>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.page === 1}
                className="p-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                disabled={pagination.page === pagination.total_pages}
                className="p-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {formOpen && (
        <PrestataireFormModal
          prestataireId={editingId ?? undefined}
          onClose={() => {
            setFormOpen(false);
            setEditingId(null);
          }}
          onSaved={handleSaved}
          onDeleted={editingId ? handleSaved : undefined}
        />
      )}
    </div>
  );
}
